import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import "../ProjectFinance/ProjectFinance.css";
import {
  getSketchDesignProjects,
  getSketchDesignProjectById,
} from "../../../api/projectApi";
import { getSketchDesignApproval } from "../../../api/projectEstimatesApi";
import { selectStyles } from "../../../utils/selectStyles";
import { UserContext } from "../../../contexts/CurrentUserContext";
import InitialData from "./InitialData/InitialData";
import CommercialOffer from "./CommercialOffer/CommercialOffer";
import Estimate from "./Estimate/Estimate";
import Fact from "./Fact/Fact";

const tabs = [
  { id: "tep", label: "ТЭП и исходные данные" },
  { id: "offer", label: "Коммерческое предложение" },
  { id: "estimate", label: "Смета на проект. работы" },
  { id: "fact", label: "Факт" },
  { id: "agreements", label: "Соглашения на физ/юр лица" },
  // { id: "bonus_pre", label: "Расчет бонусов до экспертизы" },
  // { id: "bonus_post", label: "Расчет бонусов после экспертизы" },
  // { id: "closure", label: "Закрытие проекта" },
  // { id: "evaluation", label: "Оценка" },
];

const ProjectFinanceSketchDesign = () => {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useContext(UserContext);

  // Определяем роли пользователя
  const isAdms = userData?.access_levels?.some(
    (level) => level.name === "АДМС",
  );
  const isAdmp = userData?.access_levels?.some(
    (level) => level.name === "АДМП",
  );
  const isGip = userData?.access_levels?.some((level) => level.name === "ГИП");
  const isRo = userData?.access_levels?.some((level) => level.name === "РО");
  const isFin = userData?.access_levels?.some((level) => level.name === "ФИН");

  // Проверяем специальные исключения для пользователей с полным доступом как у АДМС
  const isSpecialUser = [54, 62, 167, 11, 94, 96, 98, 99].includes(
    userData?.id,
  );
  const isSpecialGroup = userData?.departments?.some((dept) =>
    dept.groups?.some((group) => group.id === 28),
  );

  // Определяем доступные вкладки в зависимости от роли
  const getAvailableTabs = () => {
    // Специальные пользователи, ГИП и АДМС получают доступ ко всем вкладкам
    if (isSpecialUser || isSpecialGroup || isAdms || isGip) {
      return tabs;
    }
    if (isAdmp || isRo || isFin) {
      // Для АДМП, РО и ФИН показываем только вкладку "Факт"
      return tabs.filter((tab) => tab.id === "fact");
    }
    // Для остальных показываем все вкладки (как у АДМС)
    return tabs;
  };

  const availableTabs = getAvailableTabs();

  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [projectLockedByApproval, setProjectLockedByApproval] = useState(false);
  const [estimateOnApproval, setEstimateOnApproval] = useState(false);
  const [commercialOfferOnApproval, setCommercialOfferOnApproval] = useState(false);

  // Получаем параметр tab из URL
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get("tab");

    // Если есть параметр tab в URL и он доступен для пользователя
    if (tabParam && availableTabs.some((tab) => tab.id === tabParam)) {
      return tabParam;
    }

    // Проверяем, есть ли сохраненное состояние для этого проекта
    const savedTab = localStorage.getItem(
      `sketchDesignFinance_activeTab_${projectId}`,
    );

    // Если есть сохраненное состояние и оно доступно для пользователя
    if (savedTab && availableTabs.some((tab) => tab.id === savedTab)) {
      return savedTab;
    }

    // Для АДМС, ГИП и специальных пользователей возвращаем вкладку "ТЭП" по умолчанию, для остальных - первую доступную
    if (isAdms || isGip || isSpecialUser || isSpecialGroup) {
      return "tep";
    }
    return availableTabs[0]?.id || "fact";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const sketchDesignProjects = await getSketchDesignProjects();
        setProjects(
          sketchDesignProjects.map((p) => ({
            value: p.id,
            label: p.name || "Без названия",
          })),
        );
      } catch (err) {
        setError(
          "Не удалось загрузить список проектов Эскизного Проектирования",
        );
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!projectId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        // Используем API для получения проекта ЭП
        const projectData = await getSketchDesignProjectById(projectId);
        setCurrentProject(projectData);
      } catch (err) {
        setError(`Не удалось загрузить данные проекта с ID ${projectId}`);
        setCurrentProject(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProjectData();
  }, [projectId]);

  // Обновление состояния согласований (вызывается при смене проекта и после отправки/удаления согласования в дочерних компонентах)
  const refreshApprovalState = useCallback(async () => {
    if (!projectId) {
      setProjectLockedByApproval(false);
      setEstimateOnApproval(false);
      setCommercialOfferOnApproval(false);
      return;
    }
    try {
      const [estimateApproval, commercialApproval] = await Promise.all([
        getSketchDesignApproval(parseInt(projectId, 10), "estimate"),
        getSketchDesignApproval(parseInt(projectId, 10), "commercial_offer"),
      ]);
      const estimateLocked = estimateApproval != null;
      const commercialLocked = commercialApproval != null;
      setEstimateOnApproval(estimateLocked);
      setCommercialOfferOnApproval(commercialLocked);
      setProjectLockedByApproval(estimateLocked || commercialLocked);
    } catch (e) {
      console.error("Ошибка при проверке согласований ЭП:", e);
      setProjectLockedByApproval(false);
      setEstimateOnApproval(false);
      setCommercialOfferOnApproval(false);
    }
  }, [projectId]);

  // Блокировка редактирования при согласовании: GET sketch_design/approvals/get/{project_id}/{chapter}
  useEffect(() => {
    refreshApprovalState();
  }, [refreshApprovalState]);

  // Обновляем активную вкладку при изменении доступных вкладок
  useEffect(() => {
    const newInitialTab = getInitialTab();
    if (newInitialTab !== activeTab) {
      setActiveTab(newInitialTab);
    }
  }, [availableTabs]);

  // Сохраняем состояние при изменении projectId
  useEffect(() => {
    if (projectId) {
      // Сохраняем состояние только если пользователь активно переключил вкладку
      // (не при первом входе на проект)
      if (
        activeTab !== "tep" ||
        localStorage.getItem(`sketchDesignFinance_activeTab_${projectId}`)
      ) {
        localStorage.setItem(
          `sketchDesignFinance_activeTab_${projectId}`,
          activeTab,
        );
      }
    }
  }, [activeTab, projectId]);

  const handleProjectChange = (selectedOption) => {
    // Сохраняем текущее состояние перед сменой проекта
    const currentState = {
      activeTab: activeTab,
    };

    // Очищаем состояния предыдущего проекта
    if (projectId) {
      localStorage.removeItem(`sketchDesignFinance_activeTab_${projectId}`);
    }

    // Сохраняем состояние для нового проекта
    localStorage.setItem(
      `sketchDesignFinance_activeTab_${selectedOption.value}`,
      currentState.activeTab,
    );

    // Для АДМП, ГИП, РО и ФИН (но не специальных пользователей) всегда открываем вкладку "Факт"
    if (
      (isAdmp || isGip || isRo || isFin) &&
      !isSpecialUser &&
      !isSpecialGroup
    ) {
      navigate(
        `/projects/sketch-design/finance/${selectedOption.value}?tab=fact`,
      );
    } else {
      navigate(`/projects/sketch-design/finance/${selectedOption.value}`);
    }
  };

  const selectedProjectForSelect =
    projects.find((p) => p.value === parseInt(projectId)) || null;

  return (
    <div className="finance-container">
      <div className="finance-header">
        <h1 className="finance-title">
          {activeTab === "tep" && "ТЭП и исходные данные"}
          {activeTab === "offer" && "Коммерческое предложение"}
          {activeTab === "estimate" && "Смета на проект. работы"}
          {activeTab === "fact" && "Факт"}
          {activeTab === "agreements" && "Соглашения на физ/юр лица"}
          {activeTab === "bonus_pre" && "Расчет бонусов до экспертизы"}
          {activeTab === "bonus_post" && "Расчет бонусов после экспертизы"}
          {activeTab === "closure" && "Закрытие проекта"}
          {activeTab === "evaluation" && "Оценка"}
        </h1>
        <div className="finance-project-selector">
          <Select
            styles={selectStyles}
            options={projects}
            value={selectedProjectForSelect}
            onChange={handleProjectChange}
            placeholder="Выберите проект ЭП..."
            noOptionsMessage={() => "Проекты ЭП не найдены"}
            isLoading={loading && projects.length === 0}
          />
        </div>
      </div>

      <div className="finance-tabs">
        {availableTabs.map((tab) => (
          <button
            key={tab.id}
            className={`finance-tab-btn ${
              activeTab === tab.id ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab(tab.id);
              // Сохраняем активную вкладку в localStorage
              localStorage.setItem(
                `sketchDesignFinance_activeTab_${projectId}`,
                tab.id,
              );
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="finance-content">
        {activeTab === "tep" && (
          <InitialData
            projectId={projectId}
            projectLockedByApproval={projectLockedByApproval}
            estimateOnApproval={estimateOnApproval}
            commercialOfferOnApproval={commercialOfferOnApproval}
          />
        )}
        {activeTab === "offer" && (
          <CommercialOffer
            projectId={projectId}
            projectLockedByApproval={projectLockedByApproval}
            estimateOnApproval={estimateOnApproval}
            commercialOfferOnApproval={commercialOfferOnApproval}
            onApprovalStateChange={refreshApprovalState}
          />
        )}
        {activeTab === "estimate" && (
          <Estimate
            projectId={projectId}
            projectLockedByApproval={estimateOnApproval}
            estimateOnApproval={estimateOnApproval}
            commercialOfferOnApproval={commercialOfferOnApproval}
            onApprovalStateChange={refreshApprovalState}
          />
        )}
        {activeTab === "fact" && <Fact projectId={projectId} />}
        {/* Here will be other tabs */}
      </div>
    </div>
  );
};

export default ProjectFinanceSketchDesign;
